import Button from '@pelckmans/elementis/components/button';
import Checkbox from '@pelckmans/elementis/components/checkbox';
import TextField from '@pelckmans/elementis/components/text-field';
import BookwidgetIcon from '@pelckmans/elementis/icons/bookwidget';
import ClearIcon from '@pelckmans/elementis/icons/close';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../../../../contexts/ModalContext';
import LtiModal from '../../lti-modal';
import { extractLtiDeepLinkInfo, LtiResourceLinkContentType } from '../../lti-modal/utils/response-decoders';
import { BasePayload } from '../api';
import { LtiUserMaterial } from '../../../../../api/user-material';
import BaseAddMaterialModal, {
  BaseAddMaterialModalBaseProps,
  BaseFormValues,
  RenderProps,
} from '../BaseAddMaterialModal';

interface Props extends BaseAddMaterialModalBaseProps {
  id: string;
  bookwidgetUrl: string;
  material?: LtiUserMaterial;
}

export interface AddLtiMaterialPayload extends BasePayload {
  lti?: {
    ltiResource: string;
    platform: 'BookWidgets';
  };
}

export interface BookwidgetMaterial extends BaseFormValues {
  bookwidget: string;
  ltiResource: LtiResourceLinkContentType | null;
  isScorable: boolean;
  userMaxScore?: string;
}

const FORM_ID = 'add-bookwidget-form';
const BASE = 'SHARED_COMPONENTS.ADD_MATERIAL_MODAL';
const ADD_LTI_MODAL = 'add-lti-modal';

function getDefaultValues(material?: LtiUserMaterial): BookwidgetMaterial {
  return {
    name: material?.name || '',
    bookwidget: material?.lti?.name || '',
    isScorable: Boolean(material?.userMaxScore && material.userMaxScore > 0),
    userMaxScore: material?.userMaxScore?.toString(),
    ltiResource: material?.lti
      ? {
          url: material.lti.ltiResource,
          type: 'ltiResourceLink',
          lineItem: { scoreMaximum: material.userMaxScore || 1 },
        }
      : null,
  };
}

export default function AddBookwidgetMaterialModal({
  id,
  tokenAccessor,
  nodeId,
  moduleId,
  studioGroup,
  onSubmit: onSubmitCallback,
  bookwidgetUrl,
  material,
  extraPayload,
}: Props) {
  const { t } = useTranslation();
  const { showModal } = useContext(ModalContext);
  const [ltiResource, setLtiResource] = useState<LtiResourceLinkContentType | undefined>(undefined);

  function mapToPayload(data: BookwidgetMaterial): AddLtiMaterialPayload | null {
    if (material) {
      return data.name === material?.name
        ? null
        : {
            name: data.name,
          };
    }

    if (!data.ltiResource) return null;

    return {
      name: data.name,
      lti: {
        ltiResource: data.ltiResource.url,
        platform: 'BookWidgets',
      },
      ...(data.isScorable
        ? {
            maxScore: data.ltiResource.lineItem?.scoreMaximum || 1,
            userMaxScore: data.userMaxScore ? parseFloat(data.userMaxScore) : undefined,
          }
        : { maxScore: 1 }),
    };
  }

  function showLtiModal() {
    showModal(
      <LtiModal
        id={ADD_LTI_MODAL}
        loginUrl={bookwidgetUrl}
        onClose={content => {
          if (content) setLtiResource(content as LtiResourceLinkContentType);
        }}
        tokenAccessor={tokenAccessor}
        studioGroup={studioGroup}
        aria-label={t('SHARED_COMPONENTS.LTI_MODAL.A11Y_LABEL')}
        headerProps={{
          title: t('SHARED_COMPONENTS.LTI_MODAL.TITLE'),
          titleTooltip: t('SHARED_COMPONENTS.LTI_MODAL.TOOLTIP'),
        }}
        ltiResponseDecoder={extractLtiDeepLinkInfo}
      />,
    );
  }

  return (
    <BaseAddMaterialModal
      modalId={id}
      formId={FORM_ID}
      tokenAccessor={tokenAccessor}
      defaultValues={getDefaultValues(material)}
      nodeId={nodeId}
      moduleId={moduleId}
      studioGroup={studioGroup}
      onSubmit={onSubmitCallback}
      payloadMapper={mapToPayload}
      materialId={material?.id}
      extraPayload={extraPayload}
    >
      {props => (
        <BookwidgetForm
          {...props}
          showLtiModal={showLtiModal}
          ltiResource={ltiResource}
          setLtiResource={setLtiResource}
          material={material}
        />
      )}
    </BaseAddMaterialModal>
  );
}

function BookwidgetForm({
  register,
  onSubmit,
  formState: { errors },
  setValue,
  showLtiModal,
  ltiResource,
  setLtiResource,
  clearErrors,
  material,
}: RenderProps<BookwidgetMaterial> & {
  showLtiModal: () => void;
  ltiResource: LtiResourceLinkContentType | undefined;
  setLtiResource: (resource: LtiResourceLinkContentType | undefined) => void;
  material?: LtiUserMaterial;
}) {
  const { t } = useTranslation();
  const [isScorable, setIsScorable] = useState(false);
  const isEditMode = Boolean(material);

  useEffect(() => {
    if (!material) return;
    if (!material.userMaxScore) setIsScorable(false);
  }, [material, setValue]);

  useEffect(() => {
    if (!ltiResource) return;
    if (ltiResource.title) {
      setValue('bookwidget', ltiResource.title);
      clearErrors('bookwidget');
    }
    if (ltiResource.lineItem?.scoreMaximum) {
      setIsScorable(true);
      setValue('userMaxScore', ltiResource.lineItem.scoreMaximum.toString());
    } else {
      setIsScorable(false);
      setValue('userMaxScore', '');
    }

    setValue('ltiResource', ltiResource);
  }, [clearErrors, ltiResource, setValue]);

  useEffect(() => {
    setValue('isScorable', isScorable);
  }, [isScorable, setValue]);

  const bookwidgetButtonProps = isEditMode
    ? undefined
    : {
        role: 'group',
        'aria-label': undefined,
        size: 'sm' as const,
        children: [
          ltiResource ? (
            <Button
              key="clear"
              onClick={() => {
                setValue('bookwidget', '');
                setLtiResource(undefined);
              }}
              prominence="ghost"
              aria-label={t(`${BASE}.BOOKWIDGET_INPUT.A11Y_CLEAR_ACTION`)}
              startIcon={<ClearIcon />}
            />
          ) : null,
          <Button key="show-lti-modal" onClick={showLtiModal} prominence="neutral" startIcon={<BookwidgetIcon />}>
            {t(`${BASE}.BOOKWIDGET_INPUT.ACTION`)}
          </Button>,
        ],
      };

  return (
    <form id={FORM_ID} name={FORM_ID} onSubmit={onSubmit} className="form-fields" noValidate>
      <TextField
        id="name"
        key="name"
        labelText={t(`${BASE}.NAME_INPUT.LABEL`)}
        autoComplete="on"
        required
        error={!!errors.name}
        errorText={errors.name?.message}
        {...register('name', {
          required: t(`${BASE}.NAME_INPUT.EMPTY`),
        })}
      />
      <TextField
        id="bookwidget"
        key="bookwidget"
        labelText={t(`${BASE}.BOOKWIDGET_INPUT.LABEL`)}
        placeholder={t(`${BASE}.BOOKWIDGET_INPUT.PLACEHOLDER`)}
        required
        disabled
        error={!!errors.bookwidget}
        errorText={errors.bookwidget?.message}
        {...register('bookwidget', {
          required: t(`${BASE}.BOOKWIDGET_INPUT.EMPTY`),
        })}
        {...(bookwidgetButtonProps ? { buttonGroupProps: bookwidgetButtonProps } : {})}
      />
      <Checkbox
        key="isScorable"
        checked={isScorable}
        disabled={isEditMode}
        error={!!errors.isScorable}
        errorText={errors.isScorable?.message}
        {...register('isScorable', {
          validate: (value: boolean) => {
            const isScorableEnabled = ltiResource?.type === 'ltiResourceLink' && !!ltiResource.lineItem?.scoreMaximum;
            if (value && !isScorableEnabled) return t(`${BASE}.CHECKBOX_INPUT.NOT_SCORABLE`);
            return true;
          },
        })}
        onChange={e => setIsScorable(e.target.checked)}
      >
        {t(`${BASE}.IS_SCORABLE_CHECKBOX`)}
      </Checkbox>
      <TextField
        type="number"
        step="1.00"
        id="userMaxScore"
        key="userMaxScore"
        labelText={t(`${BASE}.MAXIMUM_SCORE_INPUT.LABEL`)}
        required
        error={!!errors.userMaxScore}
        errorText={errors.userMaxScore?.message}
        {...register('userMaxScore', {
          disabled: isEditMode || !isScorable,
          validate: (value?: string) => {
            if (!isScorable) return true;
            if (!value) return t(`${BASE}.MAXIMUM_SCORE_INPUT.EMPTY`);
            const parsedValue = parseFloat(value);
            if (isNaN(parsedValue) || parsedValue <= 0) return t(`${BASE}.MAXIMUM_SCORE_INPUT.INVALID`);
            return true;
          },
        })}
      />
    </form>
  );
}
